import React from 'react';
import {useIntl} from 'react-intl';
import styled, {css, keyframes} from 'styled-components';
import {Pill, OutlinedPillVariant} from '@forecast-it/design-system';
import {D} from './design-tokens';
import {ActionPhase} from './actionCardTypes';

const dotPulse = keyframes`
	0%, 100% { opacity: 1; transform: scale(1); }
	50% { opacity: 0.35; transform: scale(0.65); }
`;

// The design system Pill hardcodes font-weight: 500 (subheading/2). The Figma
// design shows the pill text in Book weight (350), so we override it here.
const PillWrapper = styled.div`
	display: contents;
	* {
		font-weight: 350 !important;
	}
`;

const PulsingDot = styled.span<{$active: boolean}>`
	display: inline-block;
	width: 8px;
	height: 8px;
	border-radius: 50%;
	flex-shrink: 0;
	background: ${({$active}) => ($active ? D.v2.accent : D.inputBorder)};
	${({$active}) =>
		$active &&
		css`
			animation: ${dotPulse} 1.2s ease-in-out infinite;
		`}
`;

type PillSpec = {labelId: string; defaultLabel: string; outlinedVariant: OutlinedPillVariant; animated: boolean};

export function phasePill(phase: ActionPhase): PillSpec {
	switch (phase) {
		case ActionPhase.Thinking:
			return {
				labelId: 'forecast_agent.action_card.status.thinking',
				defaultLabel: 'Thinking',
				outlinedVariant: OutlinedPillVariant.ACTIVE,
				animated: true,
			};
		case ActionPhase.Pending:
			return {
				labelId: 'forecast_agent.action_card.status.pending',
				defaultLabel: 'Pending Review',
				outlinedVariant: OutlinedPillVariant.NEUTRAL,
				animated: false,
			};
		case ActionPhase.Selected:
			return {
				labelId: 'forecast_agent.action_card.status.selected',
				defaultLabel: 'Selected',
				outlinedVariant: OutlinedPillVariant.ACTIVE,
				animated: false,
			};
		case ActionPhase.Confirming:
			return {
				labelId: 'forecast_agent.action_card.status.confirming',
				defaultLabel: 'Confirming',
				outlinedVariant: OutlinedPillVariant.ACTIVE,
				animated: true,
			};
		case ActionPhase.Failed:
			return {
				labelId: 'forecast_agent.action_card.status.failed',
				defaultLabel: 'Failed',
				outlinedVariant: OutlinedPillVariant.ERROR,
				animated: false,
			};
		case ActionPhase.Done:
			return {
				labelId: 'forecast_agent.action_card.status.done',
				defaultLabel: 'Done',
				outlinedVariant: OutlinedPillVariant.SUCCESS,
				animated: false,
			};
		case ActionPhase.Partial:
			// TNSR-247 (2026-06-23): server-side claim succeeded, some entities
			// failed — distinct from `failed` (no claim happened at all).
			// NEUTRAL (informational) rather than ERROR — the action DID
			// happen; the FE renders the failed-items reasons inline via
			// the partial_message Alert. (Design-system 1.12.12 doesn't
			// expose a WARNING variant for OutlinedPill.)
			return {
				labelId: 'forecast_agent.action_card.status.partial',
				defaultLabel: 'Partial',
				outlinedVariant: OutlinedPillVariant.NEUTRAL,
				animated: false,
			};
		case ActionPhase.Unresolved:
			// The write was dispatched and the response can't settle whether it
			// landed. NEUTRAL, not ERROR — an error pill would claim nothing
			// happened, which is exactly what is not established. (OutlinedPill
			// exposes ACTIVE / ERROR / SUCCESS / NEUTRAL only, so NEUTRAL is the
			// warning-weight token available, same as Partial.) The verification
			// prompt on the card body carries the "check this" weight instead.
			return {
				labelId: 'forecast_agent.action_card.status.unresolved',
				defaultLabel: 'Unconfirmed',
				outlinedVariant: OutlinedPillVariant.NEUTRAL,
				animated: false,
			};
		case ActionPhase.Expired:
			// TNSR-247 (2026-06-23): the plan's TTL passed (or a concurrent
			// confirm consumed it). Terminal; no Retry button on the card.
			return {
				labelId: 'forecast_agent.action_card.status.expired',
				defaultLabel: 'Timed Out',
				outlinedVariant: OutlinedPillVariant.NEUTRAL,
				animated: false,
			};
		default:
			// Defensive: a future ActionPhase added without a case here falls back
			// to the neutral Pending pill rather than returning undefined, which
			// StatusPill would destructure and crash on.
			return {
				labelId: 'forecast_agent.action_card.status.pending',
				defaultLabel: 'Pending Review',
				outlinedVariant: OutlinedPillVariant.NEUTRAL,
				animated: false,
			};
	}
}

export function StatusPill({phase}: {phase: ActionPhase}): JSX.Element {
	const {formatMessage} = useIntl();
	const {labelId, defaultLabel, outlinedVariant, animated} = phasePill(phase);
	const dot = animated ? (
		<span
			style={{width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center'}}
			data-testid="pill-animated-dot"
		>
			<PulsingDot $active={true} />
		</span>
	) : undefined;
	return (
		<PillWrapper>
			<Pill outlined outlinedVariant={outlinedVariant} dot={dot}>
				{formatMessage({id: labelId, defaultMessage: defaultLabel})}
			</Pill>
		</PillWrapper>
	);
}
